/**
 * HTML 淨化工具模組
 * 提供安全的 HTML 內容淨化功能，防護 XSS 攻擊
 */

import DOMPurify from 'dompurify'

/**
 * HTML 淨化配置
 */
const SANITIZE_CONFIG = {
    // 允許的 HTML 標籤白名單
    ALLOWED_TAGS: [
        'p', 'br',           // 段落和換行
        'b', 'i', 'u',       // 基本格式
        'strong', 'em',      // 語義格式
        'ul', 'ol', 'li',    // 清單
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',  // 標題
        'span'               // 行內元素
    ],

    // 允許的屬性
    ALLOWED_ATTR: ['style', 'class'], // 允許 style 和 class 屬性

    // 允許的 CSS 屬性白名單
    ALLOWED_CSS_PROPERTIES: [
        'font-size',         // 字體大小
        'color',             // 文字顏色
        'font-weight',       // 字體粗細
        'text-align',        // 文字對齊
        'line-height'        // 行高
    ],

    // 禁止的標籤
    FORBID_TAGS: [
        'script', 'iframe', 'object', 'embed',
        'form', 'input', 'button', 'select',
        'textarea', 'link', 'style', 'meta'
    ],

    // 禁止的屬性
    FORBID_ATTR: [
        'onclick', 'onload', 'onerror', 'onmouseover',
        'onmouseout', 'onfocus', 'onblur', 'onchange',
        'id', 'name'
        // 移除 'class' 以允許 CSS class 屬性
    ],

    // 危險的 CSS 模式
    DANGEROUS_CSS_PATTERNS: [
        /javascript:/gi,     // JavaScript 協議
        /data:/gi,          // Data URI 協議（防止 base64 編碼的惡意內容）
        /vbscript:/gi,      // VBScript 協議
        /expression\s*\(/gi, // IE expression
        /url\s*\(/gi,       // URL 函數
        /import/gi,         // CSS import
        /@/gi               // CSS at-rules
    ]
}

/**
 * 淨化 CSS 樣式字串
 * @param {string} styleString - 原始 CSS 樣式字串
 * @returns {string} 淨化後的 CSS 樣式字串
 */
export const sanitizeCssStyle = (styleString) => {
    if (!styleString || typeof styleString !== 'string') {
        return ''
    }

    let sanitized = styleString.trim()

    // 移除危險的 CSS 函數和協議
    SANITIZE_CONFIG.DANGEROUS_CSS_PATTERNS.forEach(pattern => {
        sanitized = sanitized.replace(pattern, '')
    })

    // 解析 CSS 屬性
    const properties = sanitized.split(';').filter(prop => prop.trim())
    const validProperties = []

    properties.forEach(prop => {
        const colonIndex = prop.indexOf(':')
        if (colonIndex === -1) return

        const property = prop.substring(0, colonIndex).trim()
        const value = prop.substring(colonIndex + 1).trim()

        if (!property || !value) return

        // 檢查是否為允許的 CSS 屬性
        if (SANITIZE_CONFIG.ALLOWED_CSS_PROPERTIES.includes(property)) {
            // 額外驗證特定屬性的值格式
            if (property === 'color') {
                // 驗證顏色值格式：hex、rgb、rgba、命名顏色（更寬鬆的格式）
                if (/^(rgb\s*\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)|rgba\s*\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*[0-9.]+\s*\)|#[0-9a-fA-F]{3,6}|[a-zA-Z]+)$/.test(value)) {
                    validProperties.push(`${property}: ${value}`)
                }
            } else if (property === 'font-size') {
                // 驗證字體大小格式：數字 + 單位
                if (/^\d+(\.\d+)?(px|em|rem|%|pt)$/.test(value)) {
                    validProperties.push(`${property}: ${value}`)
                }
            } else if (property === 'font-weight') {
                // 驗證字體粗細：數字或關鍵字
                if (/^(normal|bold|bolder|lighter|\d{3})$/.test(value)) {
                    validProperties.push(`${property}: ${value}`)
                }
            } else if (property === 'text-align') {
                // 驗證文字對齊：允許的關鍵字
                if (/^(left|right|center|justify)$/.test(value)) {
                    validProperties.push(`${property}: ${value}`)
                }
            } else if (property === 'line-height') {
                // 驗證行高：數字、數字+單位或關鍵字
                if (/^(\d+(\.\d+)?|\d+(\.\d+)?(px|em|rem|%)|normal)$/.test(value)) {
                    validProperties.push(`${property}: ${value}`)
                }
            } else {
                // 其他允許的屬性（基本驗證）
                validProperties.push(`${property}: ${value}`)
            }
        }
    })

    return validProperties.join('; ')
}



/**
 * 淨化 HTML 內容
 * @param {string} htmlContent - 原始 HTML 內容
 * @returns {string} 淨化後的安全 HTML 內容
 */
export const sanitizeHtml = (htmlContent) => {
    if (!htmlContent || typeof htmlContent !== 'string') {
        return ''
    }

    // 檢查內容大小
    const sizeCheck = checkContentSize(htmlContent)
    if (!sizeCheck.isValid) {
        throw new Error(`${HTML_PROCESSING_ERRORS.CONTENT_TOO_LARGE}: ${sizeCheck.message}`)
    }

    // 檢查標籤數量
    const tagCheck = checkTagCount(htmlContent)
    if (!tagCheck.isValid) {
        throw new Error(`${HTML_PROCESSING_ERRORS.CONTENT_TOO_LARGE}: ${tagCheck.message}`)
    }

    try {
        // 檢查 DOMPurify 是否可用
        if (!DOMPurify || typeof DOMPurify.sanitize !== 'function') {
            console.warn('DOMPurify 未正確載入，回退到純文字模式')
            return htmlContent.replace(/<[^>]*>/g, '') // 移除所有 HTML 標籤
        }

        // DOMPurify 配置
        const config = {
            ALLOWED_TAGS: SANITIZE_CONFIG.ALLOWED_TAGS,
            ALLOWED_ATTR: SANITIZE_CONFIG.ALLOWED_ATTR,
            FORBID_TAGS: SANITIZE_CONFIG.FORBID_TAGS,
            FORBID_ATTR: SANITIZE_CONFIG.FORBID_ATTR,
            ALLOW_DATA_ATTR: false,
            ALLOW_UNKNOWN_PROTOCOLS: false, // 禁止未知協議
            ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i, // 只允許安全的 URI 協議
            SAFE_FOR_TEMPLATES: false, // 防止模板注入攻擊
            WHOLE_DOCUMENT: false, // 不處理整個文件，只處理片段
            RETURN_DOM: false,
            RETURN_DOM_FRAGMENT: false,
            RETURN_DOM_IMPORT: false,
            KEEP_CONTENT: true, // 保留被移除標籤的內容
            SANITIZE_DOM: true, // 啟用 DOM 淨化
            IN_PLACE: false, // 不在原地修改，返回新的淨化內容

            // 自定義 hooks 處理 style 屬性和空標籤
            HOOKS: {
                afterSanitizeAttributes: function (node) {
                    if (node.hasAttribute && node.hasAttribute('style')) {
                        const style = node.getAttribute('style')
                        const sanitizedStyle = sanitizeCssStyle(style)
                        if (sanitizedStyle) {
                            node.setAttribute('style', sanitizedStyle)
                        } else {
                            node.removeAttribute('style')
                        }
                    }
                },

                // 處理空的 p 標籤，保留它們用於換行
                uponSanitizeElement: function (node, data) {
                    // 如果是空的 p 標籤，添加不間斷空格保持結構
                    if (data.tagName === 'p' &&
                        (!node.textContent || node.textContent.trim() === '') &&
                        node.children.length === 0) {
                        // 使用 innerHTML 而不是 textContent 來確保 HTML 實體被正確處理
                        node.innerHTML = '&nbsp;'
                        // 添加一個特殊的 data 屬性來標識這是一個空段落
                        node.setAttribute('data-empty-paragraph', 'true')
                    }
                }
            }
        }

        // 執行 HTML 淨化
        const sanitizedHtml = DOMPurify.sanitize(htmlContent, config)

        return sanitizedHtml

    } catch (error) {
        console.error('HTML 淨化過程發生錯誤:', error)
        // 回退到純文字模式
        return htmlContent.replace(/<[^>]*>/g, '')
    }
}

/**
 * 檢測內容是否包含 HTML 標籤
 * @param {string} content - 要檢測的內容
 * @returns {boolean} 如果包含 HTML 標籤則返回 true
 */
export const isHtmlContent = (content) => {
    if (!content || typeof content !== 'string') {
        return false
    }

    // 檢測是否包含 HTML 標籤的正則表達式
    const htmlTagPattern = /<[^>]*>/g
    return htmlTagPattern.test(content)
}

/**
 * HTML 處理錯誤類型
 */
export const HTML_PROCESSING_ERRORS = {
    SANITIZATION_FAILED: 'HTML_SANITIZATION_FAILED',
    DOMPURIFY_NOT_LOADED: 'DOMPURIFY_NOT_LOADED',
    INVALID_HTML_STRUCTURE: 'INVALID_HTML_STRUCTURE',
    RENDERING_ERROR: 'RENDERING_ERROR',
    CONTENT_TOO_LARGE: 'CONTENT_TOO_LARGE',
    PERFORMANCE_WARNING: 'PERFORMANCE_WARNING'
}

/**
 * 效能監控配置
 */
const PERFORMANCE_CONFIG = {
    // 內容大小限制（位元組）
    MAX_CONTENT_SIZE: 10 * 1024, // 10KB
    WARNING_CONTENT_SIZE: 5 * 1024, // 5KB 警告閾值

    // HTML 標籤數量限制
    MAX_TAG_COUNT: 300,
    WARNING_TAG_COUNT: 50
}

/**
 * 檢查內容大小是否超過限制
 * @param {string} content - 要檢查的內容
 * @returns {Object} 檢查結果
 */
export const checkContentSize = (content) => {
    const result = {
        size: 0,
        isValid: true,
        isWarning: false,
        message: ''
    }

    if (!content || typeof content !== 'string') {
        return result
    }

    // 計算內容大小（UTF-8 位元組數）
    result.size = new Blob([content]).size

    if (result.size > PERFORMANCE_CONFIG.MAX_CONTENT_SIZE) {
        result.isValid = false
        result.message = `內容大小 ${(result.size / 1024).toFixed(2)}KB 超過限制 ${(PERFORMANCE_CONFIG.MAX_CONTENT_SIZE / 1024).toFixed(2)}KB`
    } else if (result.size > PERFORMANCE_CONFIG.WARNING_CONTENT_SIZE) {
        result.isWarning = true
        result.message = `內容大小 ${(result.size / 1024).toFixed(2)}KB 接近限制，建議優化`
    }

    return result
}



/**
 * 檢查 HTML 標籤數量是否超過限制
 * @param {string} htmlContent - HTML 內容
 * @returns {Object} 檢查結果
 */
export const checkTagCount = (htmlContent) => {
    const result = {
        count: 0,
        isValid: true,
        isWarning: false,
        message: ''
    }

    // 直接計算標籤數量
    if (htmlContent && typeof htmlContent === 'string') {
        const tagMatches = htmlContent.match(/<[^>]*>/g)
        result.count = tagMatches ? tagMatches.length : 0
    }

    if (result.count > PERFORMANCE_CONFIG.MAX_TAG_COUNT) {
        result.isValid = false
        result.message = `HTML 標籤數量 ${result.count} 超過限制 ${PERFORMANCE_CONFIG.MAX_TAG_COUNT}`
    } else if (result.count > PERFORMANCE_CONFIG.WARNING_TAG_COUNT) {
        result.isWarning = true
        result.message = `HTML 標籤數量 ${result.count} 較多，可能影響效能`
    }

    return result
}





/**
 * 純文字回退處理函數
 * @param {string} htmlContent - 包含 HTML 的內容
 * @returns {string} 移除 HTML 標籤後的純文字
 */
export const fallbackToPlainText = (htmlContent) => {
    if (!htmlContent || typeof htmlContent !== 'string') {
        return ''
    }
    // 移除所有 HTML 標籤
    return htmlContent.replace(/<[^>]*>/g, '')
}





// 預設匯出主要函數
export default {
    sanitizeHtml,
    sanitizeCssStyle,
    isHtmlContent,
    fallbackToPlainText,
    checkContentSize,
    checkTagCount,
    HTML_PROCESSING_ERRORS
}